import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useCRMData } from '../context/CRMDataContext';

const getId = value => value?.id || value?.company_id || value?.companyId || null;

const getCompanyName = company => company?.name || company?.companyName || company?.title || 'Ukjent selskap';

const Clients = () => {
  const {
    companies,
    projects,
    clientPipelines,
    deals
  } = useCRMData();
  const navigate = useNavigate();

  const clients = useMemo(() => {
    const references = new Map();

    const addReference = (companyId, type) => {
      if (!companyId) return;

      const current = references.get(companyId) || {
        projects: 0,
        pipelines: 0,
        deals: 0
      };

      current[type] += 1;
      references.set(companyId, current);
    };

    (projects || []).forEach(project => {
      addReference(project.client_company_id || project.clientCompanyId, 'projects');
    });

    (clientPipelines || []).forEach(pipeline => {
      addReference(pipeline.owner_company_id || pipeline.ownerCompanyId, 'pipelines');
    });

    (deals || []).forEach(deal => {
      addReference(deal.client_company_id || deal.clientCompanyId, 'deals');
    });

    return (companies || [])
      .map(company => {
        const companyId = getId(company);
        const counts = references.get(companyId);

        if (!companyId || !counts) return null;

        return {
          ...company,
          companyId,
          counts
        };
      })
      .filter(Boolean);
  }, [companies, projects, clientPipelines, deals]);

  return (
    <div className="p-8 max-w-7xl mx-auto min-h-screen bg-slate-50 space-y-10">
      <div className="pb-8 border-b border-slate-200">
        <h1 className="text-4xl font-black text-slate-900 tracking-tight">Oppdragsgivere</h1>
        <p className="text-slate-500 font-medium text-lg mt-2">
          Selskaper som er knyttet til prosjekter, pipelines eller leads som oppdragsgiver.
        </p>
      </div>

      {clients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {clients.map(company => (
            <button
              key={company.companyId}
              type="button"
              onClick={() => navigate(`/companies/${company.companyId}`)}
              className="group text-left bg-white rounded-[2.5rem] border border-slate-200 p-8 hover:border-primary-300 shadow-sm hover:shadow-2xl hover:shadow-primary-500/10 transition-all duration-500"
            >
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm overflow-hidden group-hover:scale-110 transition-transform duration-500">
                  {company.logo ? (
                    <img src={company.logo} alt={getCompanyName(company)} className="w-full h-full object-contain p-2" />
                  ) : (
                    <SafeIcon icon="🏢" className="text-3xl text-slate-300" />
                  )}
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-black text-slate-900 leading-tight group-hover:text-primary-600 transition-colors">
                    {getCompanyName(company)}
                  </h2>
                  <span className="inline-flex mt-2 px-2.5 py-1 rounded-lg border border-blue-100 bg-blue-50 text-blue-700 text-[10px] font-black uppercase tracking-widest">
                    Oppdragsgiver
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 pt-6 mt-8 border-t border-slate-100">
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Prosjekter</p>
                  <p className="text-lg font-black text-slate-900 mt-1">{company.counts.projects}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pipelines</p>
                  <p className="text-lg font-black text-slate-900 mt-1">{company.counts.pipelines}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Leads</p>
                  <p className="text-lg font-black text-slate-900 mt-1">{company.counts.deals}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="py-16 px-6 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-200">
          <SafeIcon icon="🤝" className="w-12 h-12 mx-auto text-slate-300 mb-5" />
          <h2 className="text-2xl font-black text-slate-900">Ingen oppdragsgivere ennå</h2>
          <p className="max-w-2xl mx-auto text-slate-500 font-medium mt-3">
            En oppdragsgiver er et selskap som er koblet til et kundeprosjekt, en pipeline eller en deal.
          </p>

          <ol className="max-w-xl mx-auto mt-8 text-left space-y-3 text-slate-600 font-medium">
            <li><span className="font-black text-slate-900">1.</span> Opprett selskapet under Selskaper</li>
            <li><span className="font-black text-slate-900">2.</span> Opprett eller rediger et Kundeprosjekt</li>
            <li><span className="font-black text-slate-900">3.</span> Velg selskapet i feltet Oppdragsgiver</li>
            <li><span className="font-black text-slate-900">4.</span> Lagre prosjektet – da vises selskapet her</li>
          </ol>

          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <button
              type="button"
              onClick={() => navigate('/companies')}
              className="px-5 py-3 rounded-xl bg-primary-600 text-white font-bold hover:bg-primary-700 transition-colors"
            >
              Gå til Selskaper
            </button>
            <button
              type="button"
              onClick={() => navigate('/projects')}
              className="px-5 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-bold hover:border-primary-300 hover:text-primary-600 transition-colors"
            >
              Gå til Prosjekter
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;